import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Button, Col, DatePicker, Descriptions, Form, Input, InputNumber,
  Layout, Modal, Row, Space, Table, Tag, Tooltip, Typography, Upload, message,
} from "antd";
import { Content } from "antd/lib/layout/layout";
import { FcAlarmClock, FcComboChart, FcMindMap, FcSettings, FcVideoCall } from "react-icons/fc";
import WaterAlertModal from "./water/WaterAlertModal";

import { useGlobal } from "../../global/user";
import Water, { waitForJob } from "../../api/Water";
import Map from "../Map/index";
import VideoJS from "./VideoJS";
import WaterChart, { calibrate } from "./water/WaterChart";
import WaterConfig from "./water/WaterConfig";
import WaterStatus from "./water/WaterStatus";
import useStreamFeed from "./water/useStreamFeed";
import "./function.css";

// 水情機具（function_11）。
//
// 相對舊版的改變：
//   * 資料來自 /v1/water/*（具名欄位、帶時區的時間），不再是 wstreaming 的
//     位置陣列
//   * 直播沿用與一般影像機具相同的 /v1/streaming/*，播放邏輯抽到
//     water/useStreamFeed.js 共用
//   * 歷史紀錄以「計算工作」為單位，能看到每次的狀態、錯誤與品質指標；
//     舊版只能選時間、看不出某次為什麼沒有資料
//   * 移除油壓機具複製來的上下限／補點設定（連 alert 文字都還寫著「油壓」）
//     與已停止服務的 LINE 通報
//
// 參數設定（GRP、水尺線、PIV 格網）在 water/WaterConfig.js，由齒輪按鈕開啟。

// 鏡頭推流位址的前綴。與 function3.js 顯示的一致；部署到其他網域時用環境
// 變數覆寫，不必改程式。
const INGEST_BASE =
  process.env.REACT_APP_RTMP_INGEST_BASE || "rtmp://stream.iotwebhub.net/live/";

// 兩張設定卡片共用的樣式。抽出來是為了讓它們在視覺上明確成為一組（都是
// 「與現場設備對應、改了會影響實際運作」的欄位），與下面純文字的測站資訊
// 區隔開。
const CARD_STYLE = {
  marginBottom: 8, padding: "6px 10px",
  background: "var(--card-bg, #fafafa)", borderRadius: 4,
};
const LABEL_STYLE = {
  width: 68, fontSize: 12, color: "var(--secondary-text)",
};

// 允許前後空白、正負號與小數點；刻意不接受科學記號，座標不會那樣寫，而
// 放寬只會讓貼錯的字串被當成有效值
const NUMBER_RE = /^-?\d+(\.\d+)?$/;

const JOB_STATUS = {
  pending: { text: "等待中", color: "default" },
  capturing: { text: "錄影中", color: "processing" },
  computing: { text: "計算中", color: "processing" },
  done: { text: "完成", color: "success" },
  failed: { text: "失敗", color: "error" },
  skipped: { text: "略過", color: "warning" },
};

const TRIGGER_TEXT = {
  schedule: "排程", manual: "手動", preview: "試算", reference: "參考影像",
};

// 播放器。options 的「身分」必須穩定 —— VideoJS 會在 options 變動時重跑
// 初始化 effect，把播放器 dispose 再重建，畫面就是一次黑屏加一段不連續。
//
// 父層每 5 秒會因為健康檢查的 viewers 更新而 re-render，所以把 options 寫成
// inline 物件字面值（我第一版就是這樣）等於每 5 秒砍掉播放器重蓋。useMemo
// 鎖在真正會影響播放的 url/type 上，其餘 state 變動一概不影響播放器。
//
// poster 刻意不傳給播放器：它帶 ?b=<bust> 查詢字串，會隨重新整理改變身分，
// 而未播放時的最後影格本來就由下面的 fallback 區塊自己顯示。
//
// html5.vhs 這組參數沿用 function3.js 實測過的設定。預設值對這些現場鏡頭
// 太嚴格：碼率低又不穩，overrideNative + 緩衝式 ABR 才不會一直重協商畫質。
const Video = React.memo(function Video({
  url, type = "application/x-mpegURL", onReady,
}) {
  const options = React.useMemo(
    () => ({
      autoplay: true,
      controls: true,
      responsive: true,
      fluid: true,
      liveui: true,
      liveTracker: { trackingThreshold: 0, liveTolerance: 15 },
      sources: [{ src: url, type }],
      html5: {
        vhs: {
          withCredentials: false,
          enableLowInitialPlaylist: true,
          smoothQualityChange: true,
          overrideNative: true,
          experimentalBufferBasedABR: true,
        },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      },
    }),
    [url, type],
  );
  return <VideoJS options={options} onReady={onReady} />;
});

// 串流金鑰。平台這邊存的 streamKey 必須與鏡頭端推流設定的 stream name 完全
// 一致，否則 nginx 的 on_publish 查不到這台機具、推流會被拒絕 —— 所以換鏡頭
// 或改鏡頭設定時一定要能在這裡同步修改。
//
// 刻意不放進下面的測站資訊表單：那張表單一按「更新」會送出所有欄位，改流域
// 名稱時順手把金鑰也覆蓋掉是會讓鏡頭直接斷線的意外。這裡獨立一個儲存按鈕，
// 並且在送出前先確認。
function StreamKeyCard({ value, canEdit, onSave }) {
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  // 外部重新載入後同步顯示值
  useEffect(() => { setDraft(value || ""); }, [value]);

  const dirty = (draft || "").trim() !== (value || "");
  const ingest = `${INGEST_BASE}${value || ""}`;

  const submit = () => {
    Modal.confirm({
      title: "更換串流金鑰？",
      content: (
        <div>
          <p>鏡頭端的推流設定必須同時改成新的網址，否則這台機具會無法推流。</p>
          <p style={{ marginBottom: 0 }}>
            新的推流網址：<br />
            <code>{INGEST_BASE}{draft.trim()}</code>
          </p>
        </div>
      ),
      okText: "確定更換", cancelText: "取消",
      onOk: async () => {
        setSaving(true);
        await onSave(draft.trim());
        setSaving(false);
      },
    });
  };

  return (
    <div style={CARD_STYLE}>
      <Row gutter={8} align="middle" wrap={false}>
        <Col flex="none" style={LABEL_STYLE}>串流金鑰</Col>
        <Col flex="auto">
          <Input size="small" value={draft} disabled={!canEdit}
                 addonBefore={INGEST_BASE}
                 onChange={(event) => setDraft(event.target.value)}
                 onPressEnter={() => { if (canEdit && dirty) submit(); }} />
        </Col>
        <Col flex="none">
          <Space size={4}>
            <Tooltip title="複製鏡頭要填的推流網址">
              <Typography.Text copyable={{ text: ingest, tooltips: false }} />
            </Tooltip>
            {canEdit && (
              <Button size="small" type="primary" disabled={!dirty}
                      loading={saving} onClick={submit}>
                更換
              </Button>
            )}
          </Space>
        </Col>
      </Row>
    </div>
  );
}


// 測站座標。原本沒有任何能編輯的地方 —— latitude / longitude 早就在後端的
// 白名單裡，但表單從來沒放上對應的輸入框，所以地圖永遠停在舊值。
//
// 兩個加速輸入的設計，因為現地人員多半是從手機地圖或 GPS 裝置複製一整串
// 座標過來：
//   * 貼上 "25.020329, 121.529085" 會自動拆成兩欄（任一欄貼上都可以）
//   * 偵測到明顯顛倒（緯度落在台灣的經度範圍）時提示一鍵交換 —— 這是座標
//     輸入最常見的錯誤，而且錯了只會看到地圖跑到別的國家，不會有錯誤訊息
function LocationCard({ latitude, longitude, canEdit, onSave }) {
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLat(latitude == null ? "" : String(latitude));
    setLng(longitude == null ? "" : String(longitude));
  }, [latitude, longitude]);

  // 貼上一整串座標時自動拆欄。不論貼在哪一欄，一律按「緯度, 經度」的慣例
  // 拆解 —— 這是地圖與 GPS 裝置複製出來的順序。
  const accept = (value, field) => {
    const pair = String(value).split(/[,\s]+/).filter(Boolean);
    if (pair.length === 2 && pair.every((part) => NUMBER_RE.test(part))) {
      setLat(pair[0]);
      setLng(pair[1]);
      return;
    }
    (field === "lat" ? setLat : setLng)(value);
  };

  const latNum = Number(lat);
  const lngNum = Number(lng);
  const valid =
    NUMBER_RE.test(lat.trim()) && NUMBER_RE.test(lng.trim()) &&
    Math.abs(latNum) <= 90 && Math.abs(lngNum) <= 180;
  const dirty =
    lat.trim() !== (latitude == null ? "" : String(latitude)) ||
    lng.trim() !== (longitude == null ? "" : String(longitude));
  // 緯度不可能超過 90，所以落在 100–130 幾乎一定是把經度填進緯度欄了。
  // 這種錯不會有任何錯誤訊息，只會看到地圖跑到別的地方去。
  const swapped =
    NUMBER_RE.test(lat.trim()) && latNum > 100 && latNum < 130;

  const submit = async () => {
    setSaving(true);
    await onSave({ latitude: lat.trim(), longitude: lng.trim() });
    setSaving(false);
  };

  return (
    <div style={CARD_STYLE}>
      <Row gutter={8} align="middle" wrap={false}>
        <Col flex="none" style={LABEL_STYLE}>測站座標</Col>
        <Col flex="auto">
          <div style={{ display: "flex", gap: 6 }}>
            <Input size="small" value={lat} disabled={!canEdit}
                   addonBefore="緯度" placeholder="25.020329"
                   onChange={(event) => accept(event.target.value, "lat")}
                   onPressEnter={() => { if (canEdit && dirty && valid) submit(); }} />
            <Input size="small" value={lng} disabled={!canEdit}
                   addonBefore="經度" placeholder="121.529085"
                   onChange={(event) => accept(event.target.value, "lng")}
                   onPressEnter={() => { if (canEdit && dirty && valid) submit(); }} />
          </div>
        </Col>
        <Col flex="none">
          {canEdit && (
            <Button size="small" type="primary" loading={saving}
                    disabled={!dirty || !valid} onClick={submit}>
              更新
            </Button>
          )}
        </Col>
      </Row>
      {swapped && (
        <div style={{ marginTop: 4, fontSize: 12 }}>
          <Tag color="warning" style={{ marginRight: 4 }}>經緯度可能顛倒</Tag>
          <Button type="link" size="small" style={{ padding: 0 }}
                  onClick={() => { setLat(lng); setLng(lat); }}>
            交換兩欄
          </Button>
        </div>
      )}
      {canEdit && (
        <div style={{ marginTop: 4, fontSize: 11,
                      color: "var(--secondary-text)" }}>
          可直接貼上「25.020329, 121.529085」，會自動拆成兩欄
        </div>
      )}
    </div>
  );
}

export default function Function11({ machineID }) {
  const { user } = useGlobal();
  const canEdit = Number(user.class) >= Number(process.env.REACT_APP_EDITOR_CALSS_LV);

  const [station, setStation] = useState(null);
  const [latest, setLatest] = useState(null);
  const [videoModal, setVideoModal] = useState(false);
  const [infoModal, setInfoModal] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const [configModal, setConfigModal] = useState(false);
  const [alertModal, setAlertModal] = useState(false);

  const [range, setRange] = useState(null);
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [running, setRunning] = useState(false);

  const [imageBust, setImageBust] = useState(0);
  const [infoForm] = Form.useForm();

  const feed = useStreamFeed(machineID, { open: videoModal });

  // 影像視窗開啟期間鎖住播放 URL。HLS 位址其實不會變，但 feed.hlsUrl 來自
  // state，任何一次重新解析都可能給出同值的新字串 —— 那會讓 Video 的 useMemo
  // 失效、播放器重建。鎖成 ref 就與 state 的更新頻率徹底脫鉤。
  const lockedUrlRef = useRef(null);
  if (videoModal && feed.hlsUrl && !lockedUrlRef.current) {
    lockedUrlRef.current = feed.hlsUrl;
  } else if (!videoModal && lockedUrlRef.current) {
    lockedUrlRef.current = null;
  }

  // 播放停滯偵測。有一種失敗 VHS 永遠不會丟 error：playlist 回 200 但裡面的
  // .ts 片段 404（hls_continuous 留下的過期 playlist），或串流在播放中途死掉。
  // VHS 只會不斷在 console 印 "Problem encountered with playlist… trying
  // again"，畫面就停在「串流中」配一張凍結的影格。/v1/streaming/state 的健康
  // 檢查也救不了，因為它探測的正是那個會回 200 的 playlist。
  const handlePlayerReady = useCallback((player) => {
    let lastTime = -1;
    let lastProgressAt = Date.now();
    const gone = () =>
      typeof player.isDisposed === "function" && player.isDisposed();
    const timer = setInterval(() => {
      if (gone()) return;
      if (player.paused()) {
        lastProgressAt = Date.now();   // 使用者自己按暫停不算停滯
        return;
      }
      const now = player.currentTime() || 0;
      if (now > lastTime + 0.25) {
        lastTime = now;
        lastProgressAt = Date.now();
      } else if (Date.now() - lastProgressAt > 12000) {
        message.warning("串流已停止更新，請按重新整理");
        lastProgressAt = Date.now();   // 只提醒一次，不連續轟炸
      }
    }, 2000);
    player.on("dispose", () => clearInterval(timer));
  }, []);

  // -- 載入 ----------------------------------------------------------
  const loadStation = useCallback(async () => {
    const result = await Water.getStation(machineID);
    if (!result.ok) return;
    setStation(result.data);
    setLatest(result.data.latest);
    infoForm.setFieldsValue(result.data);
  }, [machineID, infoForm]);

  useEffect(() => { loadStation(); }, [loadStation]);

  // 列表上的數值每 5 分鐘更新一次。排程最密也是 10 分鐘一次，更頻繁沒有意義。
  useEffect(() => {
    const id = setInterval(async () => {
      const result = await Water.getLatest(machineID);
      if (result.ok) setLatest(result.data);
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [machineID]);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    const [start, end] = range || [];
    const result = await Water.getData(machineID, {
      start: start ? start.format("YYYY-MM-DD HH:mm:ss") : undefined,
      end: end ? end.format("YYYY-MM-DD HH:mm:ss") : undefined,
      limit: 2000,
    });
    setLoadingRows(false);
    if (result.ok) setRows(result.data);
    else message.error(`取得資料失敗：${result.error}`);
  }, [machineID, range]);

  const loadJobs = useCallback(async () => {
    const result = await Water.listJobs(machineID, { limit: 50 });
    if (result.ok) {
      setJobs(result.data);
      // 保持選取的那一筆是最新狀態
      setSelectedJob((prev) =>
        prev ? result.data.find((job) => job.id === prev.id) || prev : null);
    }
  }, [machineID]);

  useEffect(() => { if (infoModal) loadRows(); }, [infoModal, loadRows]);
  useEffect(() => { if (historyModal) { loadRows(); loadJobs(); } },
            [historyModal, loadRows, loadJobs]);

  // -- 手動計算 ------------------------------------------------------
  const runNow = async (type = "manual") => {
    setRunning(true);
    const created = await Water.createJob(machineID, type);
    if (!created.ok) {
      setRunning(false);
      // details 會指出是哪幾個 GRP 未完成；只顯示 error 的話使用者不知道要補哪裡
      message.warning(created.error
        + (created.details ? `：${created.details.join("；")}` : ""));
      return;
    }
    const label = TRIGGER_TEXT[type] || type;
    message.loading({ content: `${label}已排入，等待執行…`, key: "job", duration: 0 });
    const finished = await waitForJob(created.data.job_id, {
      onTick: (job) => {
        const status = JOB_STATUS[job.status];
        if (status) {
          message.loading({ content: `${label}：${status.text}`, key: "job",
                            duration: 0 });
        }
      },
    });
    setRunning(false);
    if (!finished.ok) {
      message.error({ content: finished.error, key: "job" });
    } else if (finished.data.status === "done") {
      message.success({ content: `${label}完成`, key: "job" });
      loadStation();
      loadJobs();
      loadRows();
    } else {
      message.error({ content: finished.data.error || `${label}未完成`, key: "job" });
      loadJobs();
    }
  };

  const handleInfoSubmit = async (fields) => {
    // streamKey 由上面的 StreamKeyCard 專責，這裡明確排除：loadStation 會把
    // 整個 station 物件灌進 infoForm，若順著送出去，改任何一個欄位都會連帶
    // 重寫金鑰。
    const { streamKey, ...rest } = fields;
    const result = await Water.updateStation(machineID, rest);
    if (result.ok) {
      message.success("已更新測站資訊");
      loadStation();
    } else {
      message.error(`更新失敗：${result.error}`);
    }
  };

  const handleStreamKeySave = async (streamKey) => {
    const result = await Water.updateStation(machineID, { streamKey });
    if (result.ok) {
      message.success("串流金鑰已更新，請同步修改鏡頭端設定");
      loadStation();
    } else {
      message.error(`更新失敗：${result.error}`);
    }
  };

  const handleLocationSave = async (fields) => {
    const result = await Water.updateStation(machineID, fields);
    if (result.ok) {
      message.success("已更新測站座標");
      loadStation();
    } else {
      message.error(`更新失敗：${result.error}`);
    }
  };

  // -- 歷史紀錄 ------------------------------------------------------
  const jobColumns = [
    {
      title: "時間", dataIndex: "scheduled_at", width: 150,
      render: (value) => new Date(value).toLocaleString([], {
        month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }),
    },
    {
      title: "來源", dataIndex: "trigger_type", width: 80,
      render: (value) => TRIGGER_TEXT[value] || value,
    },
    {
      title: "狀態", dataIndex: "status", width: 90,
      render: (value, job) => {
        const status = JOB_STATUS[value] || { text: value, color: "default" };
        const untrusted = job.result?.quality?.trusted === false;
        return (
          <Space size={4}>
            <Tag color={status.color}>{status.text}</Tag>
            {untrusted && <Tag color="warning">低可信</Tag>}
          </Space>
        );
      },
    },
    {
      title: "水位", width: 80, align: "right",
      render: (_, job) => fmt(calibrate(job.result?.water_level, station?.a, station?.b)),
    },
    {
      title: "流速", width: 80, align: "right",
      render: (_, job) => fmt(calibrate(job.result?.average_velocity, station?.ra, station?.rb)),
    },
    {
      title: "流量", width: 80, align: "right",
      render: (_, job) => fmt(calibrate(job.result?.discharge, station?.sa, station?.sb)),
    },
    {
      title: "說明", dataIndex: "error", ellipsis: true,
      render: (error, job) =>
        error || (job.result?.quality?.warnings || []).join("；") || "",
    },
  ];

  const media = selectedJob?.media || {};
  const mediaExpired = selectedJob?.media_dir && selectedJob?.media_deleted_at;

  return (
    <React.Fragment>
      <td>
        <div className="d-flex justify-content-center py-2">
          <div style={{ width: "72%" }}>
            <div className="d-flex justify-content-center mb-2">
              <WaterStatus station={station} latest={latest} />
            </div>
            <div className="d-flex justify-content-center">
              <Space size={4}>
                <Button shape="circle" icon={<FcVideoCall />} title="即時影像"
                        onClick={() => setVideoModal(true)} />
                <Button shape="circle" icon={<FcMindMap />} title="測站資訊"
                        onClick={() => setInfoModal(true)} />
                <Button shape="circle" icon={<FcComboChart />} title="歷史紀錄"
                        onClick={() => setHistoryModal(true)} />
                {canEdit && (
                  <Button shape="circle" icon={<FcSettings />} title="計算設定"
                          onClick={() => setConfigModal(true)} />
                )}
                <Button shape="circle" icon={<FcAlarmClock />} title="警報設定"
                        onClick={() => setAlertModal(true)} />
              </Space>
            </div>
          </div>
        </div>

        {/* ---------------- 即時影像 ---------------- */}
        <Modal title="即時影像" open={videoModal} footer={null} width={1100}
               onCancel={() => setVideoModal(false)} destroyOnClose>
          <div style={{ marginBottom: 8 }}>
            <Space>
              <Tag color={feed.status === "active" ? "green" : "default"}>
                {feed.status === "active" ? `串流中（${feed.viewers} 人）`
                  : feed.status === "loading" ? "連線中…"
                  : feed.reasonText || "未上線"}
              </Tag>
              {feed.status !== "active" && feed.retryIn !== null && (
                <span style={{ fontSize: 12, color: "var(--secondary-text)" }}>
                  {feed.retryIn} 秒後重試
                </span>
              )}
              <Button size="small" onClick={feed.refresh}>重新整理</Button>
            </Space>
          </div>
          {feed.status === "active" && (lockedUrlRef.current || feed.hlsUrl) ? (
            <Video url={lockedUrlRef.current || feed.hlsUrl}
                   onReady={handlePlayerReady} />
          ) : (
            <div style={{ aspectRatio: "4 / 3", background: "#000",
                          display: "flex", alignItems: "center",
                          justifyContent: "center", color: "#aaa" }}>
              {feed.posterUrl ? (
                <img src={feed.posterUrl} alt="最後影格"
                     style={{ width: "100%", height: "100%",
                              objectFit: "contain", opacity: 0.6 }} />
              ) : (
                <span>{feed.reasonText || "尚無影像"}</span>
              )}
            </div>
          )}
          {feed.reason === "disabled" && (
            <Alert style={{ marginTop: 8 }} type="info" showIcon
                   message="串流目前關閉，可在機具列表按「串流」開啟。" />
          )}
        </Modal>

        {/* ---------------- 測站資訊 ---------------- */}
        <Modal title="測站資訊" open={infoModal} footer={null} width={1300}
               onCancel={() => setInfoModal(false)}>
          <Layout><Content>
            <StreamKeyCard value={station?.streamKey} canEdit={canEdit}
                           onSave={handleStreamKeySave} />
            <Row gutter={16}>
              <Col flex="420px">
                <img alt="測站照片" style={{ width: "100%", borderRadius: 4 }}
                     src={Water.imageUrl(machineID, imageBust)}
                     onError={(e) => { e.currentTarget.style.display = "none"; }} />
                {canEdit && (
                  <Upload name="image" action={Water.uploadImageUrl(machineID)}
                          showUploadList={false}
                          onChange={(info) => {
                            if (info.file.status === "done") {
                              setImageBust((n) => n + 1);
                              message.success("照片已更新");
                            } else if (info.file.status === "error") {
                              message.error("照片上傳失敗");
                            }
                          }}>
                    <Button style={{ marginTop: 8 }}>上傳照片</Button>
                  </Upload>
                )}
                <div style={{ marginTop: 12 }}>
                  <h6>測站位置</h6>
                  <Map lat={station?.latitude} lng={station?.longitude} />
                </div>
              </Col>
              <Col flex="auto">
                <LocationCard latitude={station?.latitude}
                              longitude={station?.longitude}
                              canEdit={canEdit} onSave={handleLocationSave} />
                <Form form={infoForm} onFinish={handleInfoSubmit} size="small"
                      labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                  <Row>
                    <Col span={8}>
                      <Field name="main_region" label="流域(主流)" edit={canEdit} />
                      <Field name="branch_region" label="流域(支流)" edit={canEdit} />
                      <Field name="subbranch_region" label="流域(次支流)" edit={canEdit} />
                      <Field name="river_zone" label="設置河川" edit={canEdit} />
                      <Field name="catchment_area" label="集水區面積(km²)" edit={canEdit} number />
                    </Col>
                    <Col span={8}>
                      <Field name="station_elevation" label="基準高程(cm)" edit={canEdit} number />
                      <Field name="enbankment_height" label="堤頂高(cm)" edit={canEdit} number />
                    </Col>
                    <Col span={8}>
                      <Field name="nearby_station" label="附近水文站" edit={canEdit} />
                      <Field name="supervisor" label="管理單位" edit={canEdit} />
                      <Field name="device_type" label="設備型態" edit={canEdit} />
                      <Field name="observation" label="觀測項目" edit={canEdit} />
                      {canEdit && (
                        <Form.Item wrapperCol={{ offset: 10 }}>
                          <Button htmlType="submit" type="primary" size="small">
                            更新
                          </Button>
                        </Form.Item>
                      )}
                    </Col>
                  </Row>
                </Form>

                <h6 style={{ marginTop: 8 }}>水文歷線</h6>
                <WaterChart rows={rows} station={station || {}} height={300} />
                <div style={{ textAlign: "center", marginTop: 8 }}>
                  <DatePicker.RangePicker
                    showTime={{ format: "HH:mm" }} format="YYYY-MM-DD HH:mm"
                    onChange={(value) => setRange(value)} />
                  <Button style={{ marginLeft: 8 }} loading={loadingRows}
                          onClick={loadRows}>查詢</Button>
                </div>
              </Col>
            </Row>
          </Content></Layout>
        </Modal>

        {/* ---------------- 歷史紀錄 ---------------- */}
        <Modal title="歷史紀錄" open={historyModal} footer={null} width={1300}
               onCancel={() => setHistoryModal(false)}>
          <Space style={{ marginBottom: 12 }}>
            <DatePicker.RangePicker
              showTime={{ format: "HH:mm" }} format="YYYY-MM-DD HH:mm"
              onChange={(value) => setRange(value)} />
            <Button loading={loadingRows} onClick={loadRows}>查詢</Button>
            {canEdit && (
              <Button type="primary" loading={running} onClick={() => runNow("manual")}>
                立即計算
              </Button>
            )}
          </Space>

          <WaterChart rows={rows} station={station || {}} height={260} />

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={13}>
              <h6>計算工作</h6>
              <Table size="small" rowKey="id" columns={jobColumns} dataSource={jobs}
                     pagination={{ pageSize: 8, size: "small" }}
                     onRow={(job) => ({ onClick: () => setSelectedJob(job) })}
                     rowClassName={(job) =>
                       job.id === selectedJob?.id ? "ant-table-row-selected" : ""}
                     scroll={{ x: 680 }} />
            </Col>
            <Col span={11}>
              <h6>該次影像</h6>
              {!selectedJob && (
                <div style={{ color: "var(--secondary-text)" }}>
                  從左側選一筆工作以檢視影像
                </div>
              )}
              {selectedJob && mediaExpired && (
                <Alert type="info" showIcon
                       message="影像已超過保留期限並刪除，數值仍保留。" />
              )}
              {selectedJob && !mediaExpired && (
                <Space direction="vertical" style={{ width: "100%" }}>
                  {media.video && (
                    <Video url={media.video} type="video/mp4" />
                  )}
                  {media.velocity && (
                    <img alt="流速向量" src={media.velocity}
                         style={{ width: "100%", borderRadius: 4 }} />
                  )}
                  {media.waterlevel && (
                    <img alt="水位" src={media.waterlevel}
                         style={{ width: "100%", borderRadius: 4 }} />
                  )}
                  {media.reference && (
                    <img alt="參考影像" src={media.reference}
                         style={{ width: "100%", borderRadius: 4 }} />
                  )}
                  {selectedJob.result?.quality && (
                    <Descriptions size="small" column={2} bordered>
                      <Descriptions.Item label="有效向量">
                        {pct(selectedJob.result.quality.valid_ratio)}
                      </Descriptions.Item>
                      <Descriptions.Item label="相關係數">
                        {fmt(selectedJob.result.quality.mean_corr, 3)}
                      </Descriptions.Item>
                      <Descriptions.Item label="位移中位數">
                        {fmt(selectedJob.result.quality.median_disp_px, 2)} px
                      </Descriptions.Item>
                      <Descriptions.Item label="水位偵測">
                        {pct(selectedJob.result.quality.wl_detect_ratio)}
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                </Space>
              )}
            </Col>
          </Row>
        </Modal>

        {alertModal && (
          <WaterAlertModal machineID={machineID} station={station} open={alertModal}
                          canEdit={canEdit} onClose={() => setAlertModal(false)}
                          onSaved={loadStation} />
        )}

        {/* ---------------- 計算設定 ---------------- */}
        {configModal && (
          <WaterConfig machineID={machineID} station={station} open={configModal}
                       onClose={() => setConfigModal(false)}
                       onSaved={() => { loadStation(); loadJobs(); }} />
        )}
      </td>
    </React.Fragment>
  );
}

function Field({ name, label, edit, number }) {
  return (
    <Form.Item name={name} label={label} style={{ marginBottom: 4 }}>
      {number
        ? <InputNumber readOnly={!edit} bordered={edit} style={{ width: "100%" }} />
        : <Input readOnly={!edit} bordered={edit} />}
    </Form.Item>
  );
}

function fmt(value, digits = 2) {
  return value === null || value === undefined || Number.isNaN(Number(value))
    ? "—" : Number(value).toFixed(digits);
}

function pct(value) {
  return value === null || value === undefined ? "—"
    : `${Math.round(Number(value) * 100)}%`;
}